import "./CapturedPiece.scss";
import { PieceSymbol } from "chess.js";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { CustomPiecesConstants } from "../../../utils/constants";
import { useGame } from "../../../context/GameContext";
import { useMultiplayer } from "../../../context/MultiplayerContext";

// Component props: either black or white's captured pieces
interface CapturedPiecesProps {
    color: "white" | "black";
}

// Define reusable types
type ChessColor = "w" | "b";
type PieceCounts = Record<PieceSymbol, number>;
type PieceCountsByColor = Record<ChessColor, PieceCounts>;

const STARTING_PIECES: PieceCountsByColor = {
    w: { p: 8, r: 2, n: 2, b: 2, q: 1, k: 1 },
    b: { p: 8, r: 2, n: 2, b: 2, q: 1, k: 1 },
};

/**
 * Calculates which pieces have been captured by analyzing a FEN string
 *
 * FEN (Forsyth-Edwards Notation) represents a chess position as a string.
 * The first part before the space contains the board state where:
 * - Uppercase letters = white pieces (K,Q,R,B,N,P)
 * - Lowercase letters = black pieces (k,q,r,b,n,p)
 * - Numbers 1-8 = empty squares
 * - "/" separates ranks (rows)
 *
 * Example FEN: "rnbqkbnr/pppppppp/8/8/8/8/PPPPPPPP/RNBQKBNR w KQkq - 0 1"
 * This represents the starting position with all pieces present.
 *
 * Example with captures: "rnbqkbnr/pppp1ppp/8/4p3/4P3/8/PPPP1PPP/RNBQKBNR w KQkq e6 0 2"
 * This shows pawns have moved but no captures yet.
 *
 * @param fen - The FEN string representing the current board position
 * @returns Object containing arrays of captured pieces:
 *   - white: pieces that white has captured (originally black pieces)
 *   - black: pieces that black has captured (originally white pieces)
 *
 * Example return: { white: ['p', 'n'], black: ['p'] }
 * This means white captured a black pawn and knight, black captured a white pawn
 */
export function getCapturedPiecesFromFEN(fen: string): {
    white: PieceSymbol[];
    black: PieceSymbol[];
} {
    // Initialize counters for pieces currently on the board
    const currentCounts: PieceCountsByColor = {
        w: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 },
        b: { p: 0, r: 0, n: 0, b: 0, q: 0, k: 0 },
    };

    // Extract just the board position (first part before space)
    const boardPart = fen.split(" ")[0];
    const rows = boardPart.split("/"); // Split into 8 ranks

    // Count all pieces currently on the board
    rows.forEach((row) => {
        for (const char of row) {
            // Skip empty squares (represented by digits 1-8 in FEN)
            // Alternative with regex: /\d/.test(char)
            if (char >= "1" && char <= "8") continue;

            // Determine piece color: uppercase = white, lowercase = black
            const isWhite = char === char.toUpperCase();
            const piece: PieceSymbol = char.toLowerCase() as PieceSymbol;
            const color: ChessColor = isWhite ? "w" : "b";

            // Increment the count for this piece type and color
            currentCounts[color][piece]++;
        }
    });

    // Calculate promoted pieces first to adjust for pawn promotions
    const promotions = {
        w: { q: 0, r: 0, n: 0, b: 0 },
        b: { q: 0, r: 0, n: 0, b: 0 },
    };

    // For each color, calculate likely promotions
    (["w", "b"] as const).forEach((color: ChessColor) => {
        // Extra queens beyond the starting 1
        promotions[color].q = Math.max(0, currentCounts[color].q - STARTING_PIECES[color].q);
        // Extra rooks beyond the starting 2
        promotions[color].r = Math.max(0, currentCounts[color].r - STARTING_PIECES[color].r);
        // Extra knights beyond the starting 2
        promotions[color].n = Math.max(0, currentCounts[color].n - STARTING_PIECES[color].n);
        // Extra bishops beyond the starting 2
        promotions[color].b = Math.max(0, currentCounts[color].b - STARTING_PIECES[color].b);
    });

    // Calculate captured pieces by comparing current counts to starting counts, accounting for promotions
    const captured = {
        white: [] as PieceSymbol[], // Pieces white has captured (missing black pieces)
        black: [] as PieceSymbol[], // Pieces black has captured (missing white pieces)
    };

    // For each color, find missing opponent pieces
    (["w", "b"] as const).forEach((color: ChessColor) => {
        const opponent: ChessColor = color === "w" ? "b" : "w";
        const targetList = color === "w" ? captured.white : captured.black;

        // Check each piece type to see how many are missing
        (Object.keys(STARTING_PIECES.w) as PieceSymbol[]).forEach((piece) => {
            let missing = STARTING_PIECES[opponent][piece] - currentCounts[opponent][piece];

            // For pawns, subtract the number of promotions (since promoted pawns aren't captured)
            if (piece === "p") {
                const totalPromotions =
                    promotions[opponent].q +
                    promotions[opponent].r +
                    promotions[opponent].n +
                    promotions[opponent].b;
                missing = Math.max(0, missing - totalPromotions);
            }

            // Add each missing piece to the captured list
            for (let i = 0; i < missing; i++) {
                targetList.push(piece);
            }
        });
    });

    return captured;
}

// Calculate image size based on screen dimensions to match board piece sizes
export function calcCapturedPieceSize() {
    const width = window.innerWidth;
    const height = window.innerHeight;
    const minDim = Math.min(width, height);

    // Calculate board width using exact same logic as chessboard (from utils.ts)
    let boardWidth;
    if (width > 1600) boardWidth = Math.min(width * 0.35, minDim * 0.95);
    else if (width > 1200) boardWidth = Math.min(width * 0.4, minDim * 0.95);
    else if (width > 1100) boardWidth = Math.min(width * 0.4, minDim * 0.95);
    else if (width > 1000) boardWidth = Math.min(width * 0.4, minDim * 0.95);
    else if (width > 900) boardWidth = Math.min(width * 0.5, minDim * 0.95);
    else if (width > 800) boardWidth = Math.min(width * 0.5, minDim * 0.95);
    else if (width > 700) boardWidth = Math.min(width * 0.65, minDim * 0.95);
    else if (width > 600) boardWidth = Math.min(width * 0.7, minDim * 0.95);
    else boardWidth = minDim * 0.7;

    // Captured pieces should be exactly 1/8th of board width (size of one square)
    const pieceSize = Math.round(boardWidth / 8);

    // Apply reasonable min/max constraints
    return Math.max(30, Math.min(120, pieceSize));
}

// Track and update piece size on window resize
export function useCapturedPieceSize() {
    const [size, setSize] = useState(calcCapturedPieceSize());

    useEffect(() => {
        const handleResize = () => setSize(calcCapturedPieceSize());
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return size;
}
export function calcZoomMultiplier() {
    const screenWidth = window.innerWidth;

    // Increased zoom multipliers for smaller screens to make captured pieces more visible
    const breakpoints = [
        { max: 400, value: 1.0 }, // Very small mobile - increased from 0.85
        { max: 500, value: 1.05 }, // Small mobile - increased from 0.9
        { max: 700, value: 1.1 }, // Mobile - increased from 0.92
        { max: 800, value: 1.05 }, // Large mobile/small tablet - increased from 0.95
        { max: 900, value: 0.95 }, // Tablet - increased from 0.9
        { max: 1200, value: 0.92 }, // Small desktop
        { max: 1600, value: 0.95 }, // Desktop
        { max: 2560, value: 1.0 }, // Large desktop
    ];

    for (const bp of breakpoints) {
        if (screenWidth < bp.max) return bp.value;
    }

    return 1.0; // Default for ultra-wide screens
}

// Main captured pieces component
export default function CapturedPieces({ color }: CapturedPiecesProps) {
    const { gameState } = useGame();
    const { multiplayerState } = useMultiplayer();
    const captured = gameState.capturedPieces[color];
    const enemyPrefix: ChessColor = color === "white" ? "b" : "w";

    const containerClass = `captured-list captured-${color}`;
    const gridClass = color === "black" ? "captured-grid captured-black-grid" : "captured-grid";

    const size = useCapturedPieceSize();

    const gridRef = useRef<HTMLDivElement>(null);
    const [isMultiRow, setIsMultiRow] = useState(false);

    useLayoutEffect(() => {
        const grid = gridRef.current;
        if (!grid) return;

        const children = Array.from(grid.children) as HTMLElement[];

        const rowTops = new Set<number>();
        children.forEach((el) => {
            rowTops.add(Math.round(el.getBoundingClientRect().top));
        });

        setIsMultiRow(rowTops.size > 1);
    }, [captured.length, size]);

    const zoomFactor = isMultiRow
        ? CustomPiecesConstants.ZOOM_FACTOR * 1
        : CustomPiecesConstants.ZOOM_FACTOR;

    const imageSize = size * zoomFactor * calcZoomMultiplier();
    const offset = (imageSize - size) / 2;

    const povClass = multiplayerState.playerColor === "b" ? "black-pov" : "white-pov";

    return (
        <div className={`${containerClass} ${povClass} ${isMultiRow ? "multi-row" : ""}`}>
            <div className={gridClass} ref={gridRef}>
                {captured.map((p: PieceSymbol, i) => {
                    const capturedPieceImg = `${enemyPrefix}${p.toUpperCase()}`;
                    return (
                        <div
                            key={`${p}-${i}`}
                            className="captured-wrapper"
                            style={{ width: size, height: size }}
                        >
                            <img
                                src={`/images/${capturedPieceImg}.webp`}
                                className="captured-piece-img"
                                style={{
                                    width: imageSize,
                                    height: imageSize,
                                    top: -offset,
                                    left: -offset,
                                }}
                            />
                            <img
                                src={`/images/${capturedPieceImg}.webp`}
                                className="captured-piece-shadow"
                                style={{
                                    width: imageSize,
                                    height: imageSize,
                                    top: -offset,
                                    left: -offset,
                                }}
                            />
                        </div>
                    );
                })}
            </div>
        </div>
    );
}
